import cx from 'classnames'
import { Badge } from 'components/Badge'
import { RadioButtonGroup } from 'components/basic/RadioButton'
import { Checkbox } from 'components/Checkbox'
import { ClientOnly } from 'components/ClientOnly'
import { CoverModelExplanationModal } from 'components/CoverModelExplanationModal'
import { MattressModelExplanationModal } from 'components/MattressModelExplanationModal'
import { CardSelectionItemProps } from 'components/Phantom/_shop/CardSelectionItem/types'
import { ModalKey } from 'components/Phantom/_shop/Modals/modalMapping'
import { Selection } from 'components/Phantom/_shop/Selection'
import { Button } from 'components/Phantom/Button'
import { PodFamilySizeExplanationModal } from 'components/PodFamilySizeExplanationModal'
import { ModelSelectProps, ProductModelSelect } from 'components/ProductModelSelect'
import { ProductVariantSelect } from 'components/ProductVariantSelect'
import { StickyATC } from 'components/StickyATC/StickyATC'
import { Type } from 'components/Type'
import { Button as NewButton } from 'components/WebEv/Buttons'
import { BenefitsContainer } from 'components/WebEv/Shop/HelperComponents/BenefitsContainer/BenefitsContainer'
import { ShopCarousel } from 'components/WebEv/Shop/HelperComponents/ShopCarousel/ShopCarousel'
import { ContentWithSteps } from 'components/WebEv/Shop/types'
import { ShopSticky } from 'components/WebEv/ShopSticky/ShopSticky'
import { getShopPageShippingText, readShippingTimeline, ShippingStatus } from 'config/shipping_timelines'
import { AffirmLocations } from 'constants/events'
import { amHideStickyATC, amShowStickyATC } from 'events/amplitude'
import { trackAffirmLearnMoreClick, trackClickFinancingModalAffirm } from 'events/index'
import { usePathname } from 'next/navigation'
import styles from 'pagestyles/shop.module.scss'
import { PriceManager } from 'prices/priceManager'
import { Currency, FormattedPrices } from 'prices/types'
import { CartridgeProducts, PodCoverProducts, TheBaseProducts } from 'products/allProducts'
import { MultiVariantItem, Product, ProductFeaturesData, ProductImageType, ProductSelectFields, ShippingInfo, ShopSizeSelectOptions, VariantKey } from 'products/types'
import { isMemberUpgradeProductId, isPodBedProductId, isPodCoverProductId } from 'products/utils'
import React, { FC, ReactNode, useEffect, useState } from 'react'
import { Waypoint } from 'react-waypoint'
import { useIsAE, useIsAU, useIsCanada, useIsCH, useIsEULegacy, useIsMX, useIsUK } from 'stores/settings'
import { ShopSelection } from 'stores/shop'
import { RegionShort } from 'utils/internationalization'
import styles2 from './ShopMainB.module.scss'

export interface ShopProps {
	noSizeError: boolean
	selectedSize: string | undefined
	products: Product[]
	images: ProductImageType[]
	features: ProductFeaturesData
	selectedProduct: number
	selectedVariant: string
	modelSelectComponentType?: 'cards' | 'radio_buttons' | 'color_picker'
	modelSelectProps: ModelSelectProps | null
	variantSelectComponentType?: 'dropdown' | 'cards' | 'multi_variant_radio_buttons'
	variantSelectLabel: string
	dropdownItems: ShopSizeSelectOptions[]
	multiVariantItems?: MultiVariantItem[]
	shopSavingsTag?: string
	saleShopSavingsTag?: string
	onSelectVariant: (val: string | number | undefined) => void
	onAtcClick: (p: boolean | undefined, buttonID: string, addMattress: boolean) => void
	showCannotShipAlert: boolean
	currentDropdownOptions: ShopSizeSelectOptions | null
	onChangeDropdownOptions: (val: string) => void
	shipping?: ShippingInfo
	currency: Currency
	region: RegionShort
	showMembershipCheck: boolean
	membershipPrices: FormattedPrices

	displayPrice?: string
	note?: string

	onSelectModel: (index: number) => void
	onSelectVariantIndex: (index: number) => void

	cartLoading: boolean

	stickyATCTest: boolean

	currentProduct: Product
	saleEndDate: string
	numPodsAndCovers: number
	whatsInTheBoxSrc?: string
	showSubscriptionPlans: boolean

	selectMembership: (index: number) => void
	selectedMembership: number

	showMattressUpsell: boolean

	setAddMattress: (val: boolean) => void
	addMattress: boolean

	mattressUpsellPrice: number
	mattressUpsellStrikePrice: number

	showSticky: boolean
	strikePriceNumber: number
	priceNumber: number

	outOfStock?: boolean
	legKitSelectData: ShopSelection
	selectModal: (modalKey: ModalKey) => void
	showLegKitUpsell?: boolean
	legKitSelection?: string
	legKitSelect?: (legKit: string) => void
}

interface Label {
	oldPrice: string
	newPrice: string
	value: string
	label: string
	sublabel?: string
	color?: string
}

const renderVariantSelector_Cards = (props: ShopProps, dropdownItems: Label[], product: Product): ReactNode => {
	if (dropdownItems.length === 0) {
		return null
	}
	const selectedVariantIndex: number = dropdownItems.findIndex((it) => it.value === props.selectedVariant)
	const productVariants = product.variants
	const labels: ProductSelectFields[] = dropdownItems.map((it) => ({
		id: it.value,
		price: productVariants[it.value as VariantKey]?.prices.priceString ?? '',
		comparePrice: productVariants[it.value as VariantKey]?.prices.comparePriceString ?? '',
		title: it.label,
		description: '',
		onCheck: () => {
			// empty body
		},
	}))
	return (
		<div id={'size_select'}>
			<div className={styles.variant_selector_header}>
				<Type.Headline6>{props.variantSelectLabel}</Type.Headline6>
			</div>

			<ProductModelSelect
				legendLabel={props.variantSelectLabel}
				labels={labels}
				onCheck={(index) => {
					props.onSelectVariant(dropdownItems[index]?.value)
				}}
				selectedModel={selectedVariantIndex}
			/>
		</div>
	)
}

const renderVariantSelector_CardsNew = (props: ShopProps, dropdownItems: Label[], product: Product, variantName: string): ReactNode => {
	if (dropdownItems.length === 0) {
		return null
	}
	const selectedVariantIndex: number = dropdownItems.findIndex((it) => it.value === props.selectedSize)
	const productVariants = product.variants
	const labels: ProductSelectFields[] = dropdownItems.map((it) => ({
		id: it.value,
		price: productVariants[it.value as VariantKey]?.prices.priceString ?? '',
		comparePrice: productVariants[it.value as VariantKey]?.prices.comparePriceString ?? '',
		title: it.label,
		description: '',
		onCheck: () => {
			// empty body
		},
	}))

	const onCheck = (index: number): void => {
		if (props.showMattressUpsell) {
			const el = document.getElementById('membership-selector')
			if (el && window.innerWidth < 1024) {
				el.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
				})
			}
		}
		props.onSelectVariant(dropdownItems[index]?.value)
	}

	return (
		<div
			className={props.noSizeError ? styles2.size_error : ''}
			id="variant_select"
		>
			<span className={styles2.error_message}>Choose a size</span>
			<span className={cx(styles.product_type_select)}>Size</span>
			<ProductVariantSelect
				legendLabel={props.variantSelectLabel}
				labels={labels}
				onCheck={onCheck}
				selectedModel={selectedVariantIndex}
			/>
			<PodFamilySizeExplanationModal
				title={variantName}
				region={props.region}
				id={Number(product.id)}
			/>
		</div>
	)
}

const MultiVariantSelectors = (props: ShopProps): ReactNode => {
	const [selectedVariants, setSelectedVariants] = useState<string[]>(props.multiVariantItems?.map((it) => it.defaultVariantSelected) ?? [])
	if (!props.multiVariantItems) {
		return null
	}
	const variants = props.multiVariantItems

	const onVariantSelect = (value: string, groupIndex: number): void => {
		setSelectedVariants((previousVariants) => {
			const variantElements = [...previousVariants]
			variantElements[groupIndex] = value
			const _oldVariants = previousVariants.join('-')
			const _newVariants = variantElements.join('-')
			if (_oldVariants !== _newVariants) {
				props.onSelectVariant(_newVariants)
			}
			return variantElements
		})
	}

	return (
		<div>
			{variants.map((variantGroup, i) => {
				const options = variantGroup.items.map((it) => ({ groupName: variantGroup.name, ...it, component: null }))

				const getLegend = (): string => {
					if (variantGroup.name === 'colors') {
						return `${variantGroup.label} - ${variantGroup.items.find((v) => v.value === selectedVariants[i])?.label ?? ''}`
					}
					return variantGroup.label
				}

				const getDefault = (): string => {
					const defaultVariant = variantGroup.items.find((it) => it.value === variantGroup.defaultVariantSelected)
					if (defaultVariant) {
						return defaultVariant.value
					}
					return variantGroup.items[0].value
				}

				return (
					<div
						className={cx(styles.pillow_size_select, 'legend-visible', styles[variantGroup.name])}
						key={`multi_variant_${variantGroup.label}`}
					>
						<RadioButtonGroup
							options={options}
							legend={getLegend()}
							default={getDefault()}
							onSelect={(value) => {
								onVariantSelect(value, i)
							}}
						/>
					</div>
				)
			})}
		</div>
	)
}

const renderModelSelector_Cards = (props: ShopProps, productId: number): ReactNode => {
	const getExplanation = (): ReactNode => {
		if (isPodCoverProductId(productId) || isMemberUpgradeProductId(productId)) {
			return (
				<CoverModelExplanationModal
					region={props.region}
					selectedIndex={props.selectedProduct}
					prices={props.products.map((it) => it.variants[props.selectedVariant as VariantKey]?.prices.priceString ?? '')}
				/>
			)
		}

		if (isPodBedProductId(productId)) {
			return <MattressModelExplanationModal region={props.region} />
		}
		return null
	}

	const onCheck = (index: number): void => {
		if (props.showMattressUpsell) {
			const el = document.getElementById('variant_select')
			if (el && window.innerWidth < 1024) {
				el.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}
		}
		props.modelSelectProps?.onCheck(index)
	}

	return (
		<div>
			<span className={cx(styles.product_type_select)}>Model</span>
			<ProductModelSelect
				labels={props.modelSelectProps?.labels ?? []}
				legendLabel={props.modelSelectProps?.legendLabel ?? ''}
				className={props.modelSelectProps?.className ?? ''}
				selectedModel={props.selectedProduct}
				onCheck={onCheck}
			/>
			{getExplanation()}
		</div>
	)
}

const renderModelSelector_RadioButtons = (props: ShopProps): ReactNode => {
	const labels: ProductSelectFields[] =
		props.modelSelectProps?.labels.map((it) => ({
			id: it.id,
			title: it.title,
			description: '',
			onCheck: () => {
				// empty body
			},
		})) ?? []
	return (
		<div>
			<div className={styles.variant_selector_header}>
				<Type.Headline6>{props.modelSelectProps?.legendLabel ?? ''}</Type.Headline6>
			</div>

			<ProductModelSelect
				legendLabel={props.modelSelectProps?.legendLabel ?? ''}
				labels={labels}
				onCheck={(index) => props.modelSelectProps?.onCheck(index)}
				selectedModel={props.selectedProduct}
			/>
		</div>
	)
}

const renderModelSelector_ColorPicker = (props: ShopProps): ReactNode => {
	return (
		<div className={styles.color_picker_container}>
			<Type.Headline6>Color - {props.modelSelectProps?.labels[props.selectedProduct].title ?? ''}</Type.Headline6>
			<div
				className={styles.color_picker_items}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) => props.modelSelectProps?.onCheck(Number(event.target.value))}
			>
				{props.modelSelectProps?.labels.map((label, i) => {
					const isSelected = i === props.selectedProduct
					return (
						<div
							key={label.id}
							className={isSelected ? cx(styles.color_picker_item, styles.selected) : styles.color_picker_item}
							style={{
								backgroundColor: label.color,
							}}
						>
							<input
								type="radio"
								value={i}
								checked={isSelected}
							/>
						</div>
					)
				})}
			</div>
		</div>
	)
}

export const ShopMain: FC<ShopProps> = (props) => {
	const isBase = props.currentProduct.id === TheBaseProducts.TheBase.id
	const [restrictCheckout, setRestrictCheckout] = useState(isBase)
	const [showStickyATC, setShowStickyATC] = useState(false)
	const [kustomerOpen, setKustomerOpen] = useState(false)

	const pathname = usePathname()

	const carouselSlides: ContentWithSteps[] = props.images
		.filter((item) => item.type === 'image' || item.type === 'video')
		.map((item) => {
			switch (item.type) {
				case 'image':
					return {
						content: {
							type: 'image',
							data: {
								src: item.src,
								alt: item.alt,
							},
						},
						associatedSteps: [],
					}
				case 'video':
					return {
						content: {
							type: 'video-gif',
							data: {
								src: item.src,
								poster: item.thumb,
							},
						},
						associatedSteps: [],
					}
			}
		})

	useEffect(() => {
		window.Kustomer?.addListener('onOpen', () => {
			setKustomerOpen(true)
		})
		window.Kustomer?.addListener('onClose', () => {
			setKustomerOpen(false)
		})
	}, [])

	useEffect(() => {
		if (window.affirm?.ui?.refresh) {
			window.affirm.ui.refresh()
		}
	}, [props.selectedVariant])

	const clickAffirmTrigger = (event: React.MouseEvent): void => {
		event.preventDefault()
		trackClickFinancingModalAffirm(AffirmLocations.POD_SHOP)
		sessionStorage.setItem('pause_exit_intent', '1')
		const trigger = document.querySelector('.affirm-trigger-container .affirm-modal-trigger')
		if (trigger) {
			const triggerElement = trigger as HTMLElement
			triggerElement.click()
		}
	}

	const product = props.currentProduct
	const singleOptionKey = Object.keys(product.variants)[0]
	const variant = product.variants[props.selectedVariant as VariantKey] ?? product.variants[singleOptionKey as VariantKey]

	const labels =
		props.currentDropdownOptions?.items
			.map((i) => {
				const _variant = product.variants[i.value as VariantKey]
				if (!_variant) {
					return null
				}
				return { ...i, oldPrice: _variant.prices.comparePriceString, newPrice: _variant.prices.priceString }
			})
			.filter((it) => it !== null) ?? []

	const shippingObj = readShippingTimeline(variant?.id?.toString() ?? '')

	let shippingStatus: ShippingStatus = { text: '' }
	const isCanada = useIsCanada()
	const isUK = useIsUK()
	const isEULegacy = useIsEULegacy()
	const isAU = useIsAU()
	const isAE = useIsAE()
	const isCH = useIsCH()
	const isMX = useIsMX()
	if (shippingObj) {
		shippingStatus = getShopPageShippingText(readShippingTimeline(variant?.id?.toString() ?? ''), isCanada, isUK, isEULegacy, isAU, isAE, isCH, isMX)
	}

	const shippingTimeline = shippingStatus.text

	const showOneClick = isPodCoverProductId(product.id) || isPodBedProductId(product.id)

	let atcLabel = 'Add to cart'
	if (props.showMembershipCheck && props.numPodsAndCovers < 1) {
		atcLabel = showOneClick ? 'Buy now' : 'Add to cart and checkout'
	}
	if (props.outOfStock ?? false) {
		atcLabel = 'Out of Stock'
	}

	const savingsAmount = ((variant?.prices.comparePrice ?? 0) - (variant?.prices.price ?? 0)) / 100
	const upsellSavingsAmount = props.mattressUpsellStrikePrice / 100 - props.mattressUpsellPrice / 100

	const isPodInsert = props.products[props.selectedProduct].id === CartridgeProducts.Pod4Cartridge.id

	const renderProductUI = (): ReactNode => (
		<>
			{props.modelSelectComponentType === 'cards' && props.products.length > 1 && props.modelSelectProps && renderModelSelector_Cards(props, Number(product.id))}
			{props.modelSelectComponentType === 'radio_buttons' && props.modelSelectProps && renderModelSelector_RadioButtons(props)}
			{props.modelSelectComponentType === 'color_picker' && props.modelSelectProps && renderModelSelector_ColorPicker(props)}
			{props.variantSelectComponentType === 'dropdown' &&
				renderVariantSelector_CardsNew(props, labels, product, product.id === PodCoverProducts.Pod3CoverPerfect.id ? 'Pod 3 Cover with PerfectFit' : product.name)}
			{props.variantSelectComponentType === 'cards' && renderVariantSelector_Cards(props, labels, product)}
			{props.variantSelectComponentType === 'multi_variant_radio_buttons' && props.multiVariantItems && MultiVariantSelectors(props)}
			{(props.showLegKitUpsell ?? false) && (
				<Selection
					{...props.legKitSelectData}
					selectionType={'card-selection'}
					selectionProps={{
						currentSelections: {
							// We have to throw all this in because of the new shop.
							// None of this is actually used.
							'leg-kit': props.legKitSelection ?? '',
							'pod-model': 'pod_4',
							size: 'queen',
							'auto-pilot': 'no',
							'essentials-bundle': 'no',
							'eight-mattress': 'no',
							'the-base': 'no',
						},
						selectionKey: 'leg-kit',
						options: props.legKitSelectData.options.map((option) => ({
							...option,
							key: option.id,
							type: 'standard',
						})) as CardSelectionItemProps[],
						onChange: (_selectionKey: string, optionKey: string) => {
							props.legKitSelect?.(optionKey)
						},
					}}
					modalClick={props.selectModal}
					currentSelection={props.legKitSelection ?? ''}
				/>
			)}

			{PriceManager.regionHasAffirm(props.region) && (
				<div className={styles2.financing}>
					<div
						data-page-type="cart"
						data-amount={variant?.prices.price}
						onClick={() => {
							trackAffirmLearnMoreClick(AffirmLocations.POD_SHOP)
						}}
						className={cx('affirm-as-low-as', 'none', 'affirm-trigger-container')}
						tabIndex={0}
						role="button"
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault()
								trackAffirmLearnMoreClick(AffirmLocations.POD_SHOP)
							}
						}}
					/>
					Financing starting from 0% APR with $0 down with
					<svg
						viewBox="0 0 420 167"
						className={styles.affirm_logo}
					>
						<g
							fillRule="evenodd"
							clipRule="evenodd"
						>
							<path
								fill="#060809"
								d="M34.3 94.1c-11.3 0-24.4 5.4-31.5 11l6.5 13.6c5.7-5.2 14.9-9.6 23.2-9.6 7.9 0 12.2 2.6 12.2 7.9 0 3.6-2.9 5.4-8.3 6.1C16 125.7 0 131.3 0 147c0 12.4 8.9 20 22.7 20 9.9 0 18.7-5.5 22.9-12.7V165H64v-44.9c0-18.5-12.9-26-29.7-26zm-6.9 58.5c-5.2 0-7.8-2.5-7.8-6.7 0-7.8 8.7-10.4 24.6-12.1.1 10.4-7 18.8-16.8 18.8zm210.1-44.9V96.1h-18.4V165h19.7v-33.2c0-15.8 9.5-20.4 16.2-20.4 2.6 0 6.1.8 8.4 2.5l3.6-18.2c-3-1.3-6.2-1.6-8.8-1.6-10.1 0-16.5 4.5-20.7 13.6zm120.9-13.6c-10.4 0-18.2 6.2-22.3 12.1-3.8-7.7-11.7-12.1-21.3-12.1-10.4 0-17.6 5.8-21 12.4V96.1h-19V165h19.7v-35.5c0-12.7 6.7-18.8 12.9-18.8 5.6 0 10.8 3.6 10.8 13V165H338v-35.5c0-12.9 6.5-18.8 13-18.8 5.2 0 10.7 3.8 10.7 12.9V165h19.7v-47.6c0-15.5-10.4-23.3-23-23.3zM133.6 91v5.1h-29.9v-7c0-9.1 5.2-11.7 9.7-11.7 2.6 0 6 .6 8.8 2.2l6.1-13.9c-3.6-2.1-9.5-4-17.4-4-12.6 0-26.9 7.1-26.9 29.4v5.1H72.6v15.2H84V165h19.7v-53.7h29.9V165h19.7v-53.7h17.9V96.1h-17.9v-7c0-9.1 5.2-11.7 9.7-11.7 5 0 8.8 2.2 8.8 2.2l6.1-13.9s-6.2-4-17.4-4c-12.5-.1-26.9 7-26.9 29.3zm49.3 5.1h19.7V165h-19.7z"
							/>
							<path
								fill="#0fa0ea"
								d="M297.7 0c-53.2 0-100.6 36.9-114 84.4H203C214.2 49 252.3 18 297.7 18c55.2 0 102.8 42 102.8 107.4 0 14.7-1.9 27.9-5.5 39.6h18.7l.2-.6c3.1-12.1 4.6-25.2 4.6-39C418.5 52.5 365.4 0 297.7 0"
							/>
						</g>
					</svg>
					<Button.Empty
						id={'affirm-pre-qualify-button'}
						onClick={clickAffirmTrigger}
						className={styles2.trigger_p}
						aria-label={'Open Financing Modal'}
					>
						Pre-qualify now
					</Button.Empty>
					{/* <div className={styles2.AffirmLineBF}>
						<AffirmLineBF
							newFinancing={true}
							id={'cart-affirm-trigger-container'}
							monthlyPrice={PriceManager.formatPriceToCurrencyNoDecimal(monthlyPrice, props.currency)}
							currentPrice={variant.prices.price / 100}
						/>
					</div> */}
				</div>
			)}

			{(props.note ?? '') && <div className={styles.note}>{props.note}</div>}

			{isBase && (
				<div className={styles.base_checkbox_container}>
					<Checkbox
						checked={!restrictCheckout}
						onChange={(checked) => {
							setRestrictCheckout(!checked)
						}}
						label={'I acknowledge that the base needs to be paired with a Pod 4 to be fully functional. The base will not function without a Pod and Autopilot.'}
						id={'the-base-checkbox'}
					/>
					<Type.Body2
						onClick={() => {
							setRestrictCheckout((prev) => !prev)
						}}
					>
						I acknowledge that the base needs to be paired with a Pod 4 to be fully functional. The base will not function without a Pod and Autopilot.
					</Type.Body2>
				</div>
			)}

			<Waypoint
				onPositionChange={(position: { currentPosition: 'above' | 'below' }) => {
					if (props.stickyATCTest) {
						if (position.currentPosition === 'above') {
							amShowStickyATC()
							setShowStickyATC(true)
						} else {
							amHideStickyATC()
							setShowStickyATC(false)
						}
					}
				}}
				topOffset={100}
			>
				<div className={cx(styles.atc_container, { [styles.atc_container_sticky_shown]: props.stickyATCTest })}>
					<NewButton.Primary
						onClick={() => {
							props.onAtcClick(props.showMembershipCheck ? true : undefined, 'add-to-cart-button', props.addMattress)
						}}
						className={cx(styles.atc_button)}
						id="add-to-cart-button"
						size={'large'}
						loading={props.cartLoading}
						disabled={(props.outOfStock ?? false) || restrictCheckout ? true : props.cartLoading}
					>
						{atcLabel}
					</NewButton.Primary>
				</div>
			</Waypoint>
			{shippingTimeline && (
				<div className={styles.timeline}>
					<ClientOnly>
						<BenefitsContainer
							warrantyAmount={0}
							shippingTimeline={shippingTimeline}
							hasFreeReturns={!isPodInsert}
							hasFreeTrial={!isPodInsert}
						/>
					</ClientOnly>
				</div>
			)}
		</>
	)

	const atcSection = renderProductUI()

	const stickyATC = props.stickyATCTest
	let showStickyATCTestCheck = false

	if (stickyATC && !kustomerOpen) {
		showStickyATCTestCheck = showStickyATC
	}

	if (!kustomerOpen && typeof document !== 'undefined' && stickyATC) {
		const kustomer = document.getElementById('kustomer-ui-sdk-iframe')
		if (kustomer) {
			setTimeout(() => {
				kustomer.style.visibility = 'hidden'
				kustomer.style.display = 'none'
			}, 250)
		}
	}

	return (
		<>
			{props.showSticky && (
				<ShopSticky
					stickyName={props.currentProduct.name}
					promoText={isPodCoverProductId(product.id) ? '' : `${PriceManager.formatPriceToRegionNoDecimal((props.strikePriceNumber - props.priceNumber) / 100, props.region)} off`}
					sizeText={`${props.currentProduct.variants[props.selectedVariant as VariantKey]?.name ?? ''} Size`}
					price={PriceManager.formatPriceToRegionNoDecimal(props.priceNumber / 100, props.region)}
					strikePrice={PriceManager.formatPriceToRegionNoDecimal(props.strikePriceNumber / 100, props.region)}
					ctaClick={() => {
						props.onAtcClick(props.showMembershipCheck ? true : undefined, 'top-stick-atc-button', props.addMattress)
					}}
					ctaCopy={atcLabel}
					loading={props.cartLoading}
					disabled={props.cartLoading || restrictCheckout}
				/>
			)}
			<div className={cx(styles.shop)}>
				<ShopCarousel
					items={carouselSlides}
					className={styles.shop_carousel}
				/>

				<div className={styles.atc}>
					<div className={styles.right_side_normal}>
						<div className={styles.product_info}>
							<Type.Headline3
								as={'h1'}
								className={styles.title}
							>
								{' '}
								{product.name}{' '}
								{(props.saleShopSavingsTag ?? '') && props.showMattressUpsell && upsellSavingsAmount > 0 ? <span className={styles.sa_vings_tag3}>{props.saleShopSavingsTag}</span> : null}
							</Type.Headline3>

							<Type.Headline5
								as={'p'}
								className={styles.money_amount_tags}
							>
								{variant?.prices.comparePriceString !== variant?.prices.priceString && (
									<del className={cx(styles.price_tag_compare, { [styles.google_compare_tag]: pathname?.includes('shop-pod-cover') ?? false })}>{variant?.prices.comparePriceString}</del>
								)}
								<span className={cx(styles.price_tag_price, { [styles.google_price_tag]: pathname?.includes('shop-pod-cover') ?? false })}>{variant?.prices.priceString}</span>

								{savingsAmount > 0 && <Badge.Orange type={'discount'}>{PriceManager.formatPriceToCurrencyNoDecimal(savingsAmount, props.currency)} off</Badge.Orange>}
							</Type.Headline5>
							<Type.Body2>
								{product.description}
								{isBase && (
									<b>
										<br />
										<br />
										Please note: The Base is designed exclusively for use with the Pod 4, relying on its system for connectivity and operation. Without a Pod, the Base's features cannot be activated
										or utilized.
									</b>
								)}
							</Type.Body2>
						</div>

						{atcSection}
					</div>
				</div>

				{stickyATC ? (
					<div className={cx(styles.sticky_atc_hidden, { [styles.sticky_atc_show]: showStickyATCTestCheck })}>
						<StickyATC
							price={(variant?.prices.price ?? 0) + (props.addMattress ? props.mattressUpsellPrice : 0)}
							comparePrice={(variant?.prices.comparePrice ?? 0) + (props.addMattress ? props.mattressUpsellStrikePrice : 0)}
							currency={props.currency}
							atcClick={() => {
								props.onAtcClick(props.showMembershipCheck ? true : undefined, 'add-to-cart-button', props.addMattress)
							}}
							cartLoading={props.cartLoading}
							buyNowLoading={props.cartLoading}
							showFinancing={!isUK && !isEULegacy && !isAU && (props.displayPrice === undefined || props.displayPrice === '')}
							size={variant?.name ?? ''}
							selectedSize={props.selectedSize ?? ''}
							outOfStock={props.outOfStock ?? false}
							disabled={restrictCheckout || props.cartLoading}
						/>
					</div>
				) : null}
			</div>
		</>
	)
}
